import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Calendar,
  Clock,
  MapPin,
  ChevronRight,
  Loader2,
  Sun,
  Sunrise,
  CheckCircle2,
  Play,
  Car,
  Gauge,
  Home,
} from 'lucide-react';
import { formatTime, cn } from '../lib/utils';
import { toast } from 'sonner';

const StaffMyDayPage = () => {
  const { api, user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewDate, setViewDate] = useState('today'); // 'today' or 'tomorrow'
  
  // Odometer state
  const [showOdometerDialog, setShowOdometerDialog] = useState(false);
  const [odometerReading, setOdometerReading] = useState('');
  const [odometerType, setOdometerType] = useState(null); // 'leave_home' or 'arrive_home'
  const [lastOdometerReading, setLastOdometerReading] = useState(null);
  const [todayTrips, setTodayTrips] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchLastOdometer = useCallback(async () => {
    try {
      const response = await api().get('/odometer/last');
      setLastOdometerReading(response.data.reading);
    } catch (error) {
      console.error('Failed to fetch last odometer:', error);
    }
  }, [api]);

  const fetchTodayTrips = useCallback(async () => {
    try {
      const response = await api().get('/odometer/trips/today');
      setTodayTrips(response.data);
    } catch (error) {
      console.error('Failed to fetch today trips:', error);
    }
  }, [api]);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      // Get bookings for selected date
      const dateOffset = viewDate === 'tomorrow' ? 1 : 0;
      const targetDate = new Date();
      targetDate.setDate(targetDate.getDate() + dateOffset);
      const dateStr = targetDate.toISOString().split('T')[0];
      
      const response = await api().get('/bookings/', {
        params: {
          date_from: dateStr,
          date_to: dateStr,
          assigned_to: user.id
        }
      });
      
      // Sort by time window
      const sorted = (response.data.bookings || []).sort((a, b) => {
        const timeA = a.time_window_start || '00:00';
        const timeB = b.time_window_start || '00:00';
        return timeA.localeCompare(timeB);
      });
      
      setBookings(sorted);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      toast.error('Failed to load your jobs');
    } finally {
      setLoading(false);
    }
  }, [api, user?.id, viewDate]);

  useEffect(() => {
    fetchBookings();
    fetchLastOdometer();
    fetchTodayTrips();
  }, [fetchBookings, fetchLastOdometer, fetchTodayTrips]);

  const handleOdometerSubmit = async () => {
    if (!odometerReading || isNaN(parseFloat(odometerReading))) {
      toast.error('Please enter a valid odometer reading');
      return;
    }
    
    const reading = parseFloat(odometerReading);
    
    if (lastOdometerReading && reading < lastOdometerReading.reading_km) {
      toast.error(`Reading must be higher than last reading (${lastOdometerReading.reading_km} km)`);
      return;
    }
    
    setActionLoading(true);
    try {
      await api().post('/odometer/reading', {
        reading_type: odometerType,
        reading_km: reading
      });
      
      if (lastOdometerReading && odometerType === 'arrive_home') {
        const distance = reading - lastOdometerReading.reading_km;
        toast.success(`Recorded! Today's trip: ${distance.toFixed(1)} km`);
      } else {
        toast.success('Odometer reading recorded!');
      }
      
      setShowOdometerDialog(false);
      setOdometerReading('');
      fetchLastOdometer();
      fetchTodayTrips();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record odometer');
    } finally {
      setActionLoading(false);
    }
  };

  const getJobState = (booking) => {
    if (booking.status === 'completed') return 'completed';
    if (booking.status === 'in_progress' || booking.checkin_time) return 'on_site';
    if (booking.status === 'accepted') return 'ready';
    if (booking.status === 'assigned') return 'pending_accept';
    return 'not_started';
  };

  const getJobStateDisplay = (state) => {
    switch (state) {
      case 'completed':
        return { label: 'Completed', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 };
      case 'on_site':
        return { label: 'On Site', color: 'bg-amber-100 text-amber-700 animate-pulse', icon: Play };
      case 'ready':
        return { label: 'Ready', color: 'bg-blue-100 text-blue-700', icon: Clock };
      case 'pending_accept':
        return { label: 'Accept Required', color: 'bg-purple-100 text-purple-700', icon: Clock };
      default:
        return { label: 'Not Started', color: 'bg-zinc-100 text-zinc-600', icon: Clock };
    }
  };

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const dateLabel = viewDate === 'today' 
    ? new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })
    : new Date(Date.now() + 86400000).toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' });

  // Count jobs by state
  const completedCount = bookings.filter(b => getJobState(b) === 'completed').length;
  const onSiteCount = bookings.filter(b => getJobState(b) === 'on_site').length;
  const remainingCount = bookings.length - completedCount - onSiteCount;

  return (
    <div className="space-y-6 pb-24 md:pb-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-zinc-900 tracking-tight">
            {greeting()}, {user?.full_name?.split(' ')[0]}
          </h1>
          <p className="text-zinc-500 mt-1 flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            {dateLabel}
          </p>
        </div>
        
        {/* Today/Tomorrow Toggle */}
        <div className="flex bg-zinc-100 rounded-lg p-1">
          <button
            onClick={() => setViewDate('today')}
            className={cn(
              'px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2',
              viewDate === 'today' 
                ? 'bg-white text-zinc-900 shadow-sm' 
                : 'text-zinc-600 hover:text-zinc-900'
            )}
            data-testid="today-toggle"
          >
            <Sun className="h-4 w-4" />
            Today
          </button>
          <button
            onClick={() => setViewDate('tomorrow')}
            className={cn(
              'px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2',
              viewDate === 'tomorrow' 
                ? 'bg-white text-zinc-900 shadow-sm' 
                : 'text-zinc-600 hover:text-zinc-900'
            )}
            data-testid="tomorrow-toggle"
          >
            <Sunrise className="h-4 w-4" />
            Tomorrow
          </button>
        </div>
      </div>

      {/* Odometer Tracking Card */}
      {viewDate === 'today' && (
        <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <Gauge className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium text-zinc-900">Odometer Tracking</h3>
                  {todayTrips ? (
                    <p className="text-sm text-zinc-600">
                      Today: <span className="font-semibold text-primary">{todayTrips.total_km.toFixed(1)} km</span>
                      {todayTrips.trips.length > 0 && ` (${todayTrips.trips.length} trip${todayTrips.trips.length !== 1 ? 's' : ''})`}
                    </p>
                  ) : (
                    <p className="text-sm text-zinc-500">Track your kilometers</p>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setOdometerType('leave_home');
                    setOdometerReading('');
                    setShowOdometerDialog(true);
                  }}
                  className="bg-white"
                  data-testid="leave-home-btn"
                >
                  <Home className="h-4 w-4 mr-1" />
                  Leave Home
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setOdometerType('arrive_home');
                    setOdometerReading('');
                    setShowOdometerDialog(true);
                  }}
                  className="bg-white"
                  data-testid="arrive-home-btn"
                >
                  <Home className="h-4 w-4 mr-1" />
                  Arrive Home
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Bar */}
      {bookings.length > 0 && (
        <div className="flex items-center gap-3 text-sm">
          <span className="text-zinc-600">{bookings.length} job{bookings.length !== 1 ? 's' : ''}</span>
          {completedCount > 0 && (
            <Badge className="bg-emerald-100 text-emerald-700">
              {completedCount} done
            </Badge>
          )}
          {onSiteCount > 0 && (
            <Badge className="bg-amber-100 text-amber-700 animate-pulse">
              {onSiteCount} on site
            </Badge>
          )}
          {remainingCount > 0 && (
            <Badge className="bg-zinc-100 text-zinc-600">
              {remainingCount} remaining
            </Badge>
          )}
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
        </div>
      ) : bookings.length === 0 ? (
        /* Empty State */
        <Card className="border-zinc-100">
          <CardContent className="py-16 text-center">
            <div className="mx-auto h-20 w-20 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
              <CheckCircle2 className="h-10 w-10 text-emerald-500" />
            </div>
            <h3 className="text-xl font-medium text-zinc-900 mb-2">
              {viewDate === 'today' ? 'No jobs today' : 'No jobs tomorrow'}
            </h3>
            <p className="text-zinc-500">Enjoy your time off!</p>
          </CardContent>
        </Card>
      ) : (
        /* Job Cards */
        <div className="space-y-3">
          {bookings.map((booking, index) => {
            const state = getJobState(booking);
            const stateDisplay = getJobStateDisplay(state);
            const StateIcon = stateDisplay.icon;
            
            return (
              <Link
                key={booking.id}
                to={`/my-jobs/${booking.id}`}
                data-testid={`job-card-${booking.id}`}
              >
                <Card className={cn(
                  'border-zinc-100 hover:border-zinc-300 transition-all hover:shadow-md',
                  state === 'on_site' && 'ring-2 ring-amber-400 border-amber-200'
                )}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      {/* Job Number */}
                      <div className={cn(
                        'h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 font-semibold',
                        state === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                        state === 'on_site' ? 'bg-amber-100 text-amber-700' :
                        'bg-zinc-100 text-zinc-600'
                      )}>
                        {index + 1}
                      </div>
                      
                      {/* Job Details */}
                      <div className="flex-1 min-w-0">
                        {/* Top Row: Client + State */}
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h3 className="font-semibold text-zinc-900 truncate">
                            {booking.client_name}
                          </h3>
                          <Badge className={cn('flex-shrink-0', stateDisplay.color)}>
                            <StateIcon className="h-3 w-3 mr-1" />
                            {stateDisplay.label}
                          </Badge>
                        </div>
                        
                        {/* Address */}
                        <p className="text-sm text-zinc-600 flex items-center gap-1 mb-1">
                          <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                          <span className="truncate">{booking.service_address}</span>
                        </p>
                        
                        {/* Time Window */}
                        {booking.time_window_start && (
                          <p className="text-sm text-zinc-500 flex items-center gap-1 mb-2">
                            <Clock className="h-3.5 w-3.5" />
                            {booking.time_window_start} - {booking.time_window_end || 'TBC'}
                          </p>
                        )}
                        
                        {/* Job Scope Preview */}
                        {booking.job_details && (
                          <p className="text-sm text-zinc-500 line-clamp-1">
                            {booking.job_details}
                          </p>
                        )}
                        
                        {/* On Site Timer */}
                        {state === 'on_site' && booking.checkin_time && (
                          <div className="mt-2 flex items-center gap-2 text-amber-700">
                            <Play className="h-4 w-4" />
                            <span className="text-sm font-medium">
                              On site since {formatTime(booking.checkin_time)}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {/* Chevron */}
                      <ChevronRight className="h-5 w-5 text-zinc-400 flex-shrink-0 mt-2" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}

      {/* Odometer Dialog */}
      <Dialog open={showOdometerDialog} onOpenChange={setShowOdometerDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gauge className="h-5 w-5 text-primary" />
              {odometerType === 'leave_home' ? 'Leaving Home' : 'Arriving Home'}
            </DialogTitle>
            <DialogDescription>
              Enter your current odometer reading to track your travel distance.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {lastOdometerReading && (
              <div className="bg-zinc-50 rounded-lg p-3 text-sm">
                <p className="text-zinc-500">Last reading:</p>
                <p className="font-semibold text-lg text-zinc-900">{lastOdometerReading.reading_km.toLocaleString()} km</p>
                <p className="text-xs text-zinc-400">
                  {new Date(lastOdometerReading.recorded_at).toLocaleString('en-AU', {
                    dateStyle: 'short',
                    timeStyle: 'short'
                  })}
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="odometer-home">Current Odometer (km)</Label>
              <div className="relative">
                <Car className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                <Input
                  id="odometer-home"
                  type="number"
                  placeholder="e.g., 45678"
                  value={odometerReading}
                  onChange={(e) => setOdometerReading(e.target.value)}
                  className="pl-10 text-lg h-12"
                  autoFocus
                  data-testid="odometer-home-input"
                />
              </div>
              {lastOdometerReading && odometerReading && parseFloat(odometerReading) > lastOdometerReading.reading_km && (
                <p className="text-sm text-emerald-600">
                  Distance traveled: {(parseFloat(odometerReading) - lastOdometerReading.reading_km).toFixed(1)} km
                </p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowOdometerDialog(false)}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleOdometerSubmit}
              disabled={actionLoading || !odometerReading}
              data-testid="submit-odometer-home-btn"
            >
              {actionLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-2" />
              )}
              Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffMyDayPage;
